import { TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from './app-router';

/**
 * Tryb offline (MVP §14, Etap 10): bez sieci NICZEGO nie zapisujemy ani nie zatwierdzamy lokalnie i niczego nie
 * kolejkujemy „na później”. Każda mutacja tRPC (zatwierdzenie, wysyłka, zapis) jest od razu odrzucana z jasnym
 * komunikatem, a React Query nie wstrzymuje jej do powrotu sieci (`networkMode: 'always'` w providers.tsx) — inaczej
 * kliknięcie „Zatwierdź” offline wykonałoby się samo po odzyskaniu połączenia. Odczyty działają z pamięci podręcznej
 * zapytań; service worker NIE przechowuje odpowiedzi API (tylko powłokę aplikacji).
 */

export const OFFLINE_MESSAGE =
  'Brak połączenia. Bez sieci niczego nie zapisujemy ani nie zatwierdzamy — spróbuj, gdy połączenie wróci.';

/** navigator.onLine === false (przeglądarka wie, że nie ma sieci). */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function offlineError(): TRPCClientError<AppRouter> {
  return TRPCClientError.from<AppRouter>({
    error: { code: -32603, message: OFFLINE_MESSAGE, data: { code: 'CLIENT_OFFLINE', httpStatus: 0, offline: true } },
  });
}

export function isOfflineError(err: unknown): boolean {
  const data = (err as { data?: { offline?: unknown } } | null)?.data;
  return Boolean(data && data.offline === true);
}

/** Pierwszy link klienta tRPC: mutacja offline nie wychodzi z przeglądarki. */
export const offlineLink: TRPCLink<AppRouter> = () => {
  return ({ op, next }) =>
    observable((observer) => {
      if (op.type === 'mutation' && isOffline()) {
        observer.error(offlineError());
        return;
      }
      const subscription = next(op).subscribe(observer);
      return () => subscription.unsubscribe();
    });
};
